// PKCE helpers for OIDC Authorization Code Flow with PKCE
// Spec: RFC 7636

import { getApiBase } from '@/lib/deployment'

const PKCE_VERIFIER_KEY = 'aip_dns_pkce_verifier'
const PKCE_STATE_KEY = 'aip_dns_pkce_state'

export function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return base64urlEncode(array)
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return base64urlEncode(new Uint8Array(hash))
}

export function generateState(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return base64urlEncode(array)
}

export function savePkceParams(verifier: string, state: string): void {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)
  sessionStorage.setItem(PKCE_STATE_KEY, state)
}

export function getPkceParams(): { verifier: string | null; state: string | null } {
  return {
    verifier: sessionStorage.getItem(PKCE_VERIFIER_KEY),
    state: sessionStorage.getItem(PKCE_STATE_KEY),
  }
}

export function clearPkceParams(): void {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)
  sessionStorage.removeItem(PKCE_STATE_KEY)
}

function base64urlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

// The SPA is mounted at /ui (see vite.config.ts `base` and the FastAPI
// `app.mount("/ui", ...)`).  Both the OAuth authorisation request and the
// token-exchange call MUST send the same redirect_uri, so this function is
// called from both auth.tsx (build URL) and callback.tsx (exchange code).
const SPA_BASE = '/ui'

/**
 * Compute the OAuth2 redirect URI.
 *
 * Priority:
 *   1. VITE_OAUTH_REDIRECT_URI env var (explicit override, set at build time)
 *   2. Dynamic: origin + everything up to and including the /ui mount + /oauth/callback
 */
export function computeRedirectUri(): string {
  if (import.meta.env.VITE_OAUTH_REDIRECT_URI) {
    return import.meta.env.VITE_OAUTH_REDIRECT_URI
  }
  const { origin, pathname } = window.location
  const idx = pathname.indexOf(SPA_BASE)
  const prefix = idx >= 0 ? pathname.slice(0, idx + SPA_BASE.length) : SPA_BASE
  return `${origin}${prefix}/oauth/callback`
}

export interface ExchangeCodeParams {
  tokenEndpoint: string
  code: string
  codeVerifier: string
  clientId: string
  redirectUri: string
}

export interface TokenResponse {
  access_token: string
  refresh_token?: string
  id_token?: string
  expires_in?: number
  token_type?: string
  scope?: string
}

export async function exchangeCode(params: ExchangeCodeParams): Promise<TokenResponse> {
  // Use backend proxy to avoid CORS issues with PingFederate token endpoint
  // Backend endpoint: POST /auth/token/exchange
  // This is a workaround for when PingFederate CORS is not configured.
  // Proper fix: configure PingFederate OAuth client CORS settings.

  const API_BASE = getApiBase()

  const response = await fetch(`${API_BASE}/auth/token/exchange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code: params.code,
      code_verifier: params.codeVerifier,
      redirect_uri: params.redirectUri,
      client_id: params.clientId,
    }),
  })

  if (!response.ok) {
    let errorMessage = `Token exchange failed: HTTP ${response.status}`
    try {
      const errorData = await response.json()
      errorMessage = errorData.error_description || errorData.error || errorMessage
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(errorMessage)
  }

  const data = await response.json()

  if (!data.access_token) {
    throw new Error('No access token received from token endpoint')
  }

  return data as TokenResponse
}
